import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { NgbActiveModal, NgbNav } from '@ng-bootstrap/ng-bootstrap';
import { StepService } from 'services/professional/step/step.service';
import { MessageQueryService, SmartModalService } from '@certemy/ui-components';
import { DynamicFormInfo } from 'interfaces/dynamic-form-info.interface';
import { ComputedFrom } from '@certemy/common';
import { isStepSubmitted } from 'utils/step.helpers';
import { DynamicFormField, DynamicFormFieldOption, DynamicFormSection } from 'interfaces/dynamic-form-field.interface';
import { DynamicFormContentDetails } from '../dynamic-details/modal-content/dynamic-form-content-details/dynamic-form-content-details.component';
import { SignatureComponent } from 'components/signature/signature.component';
import { DynamicFormComponent } from 'components/dynamic-form/dynamic-form.component';
import { STEP_TYPES, STEP_TYPES_NAMES } from 'constants/status.constants';
import { delayWhen, filter, switchMap, take, tap } from 'rxjs/operators';
import { constants, VERIFICATION_TYPE } from 'constants/constants';
import _last from 'lodash-es/last';
import _uniq from 'lodash-es/uniq';
import {
  additionalMappedStepFields,
  additionalMappedStepFieldsWithValues,
  addTooltipToSections,
  AllowedDataInterface,
  birthDateValidator,
  clearDuplicateErrorsOnTab,
  countryPostalCodeValidator,
  getControlById,
  getElementById,
  getRecordIndex,
  lastOfVisible,
  noSpacesValidator,
  ONBOARDING_FIELD_IDS_MAP,
  ONBOARDING_HELP_TEXT,
  ONBOARDING_MONITORED_BLOCK_ENUM,
  ONBOARDING_TAB_ID,
  ONBOARDING_TAB_TITLE,
  ONBOARDING_TAB_TYPES,
  ONBOARDING_VALIDATION_MESSAGE,
  OnboardingCompletePayload,
  OnboardingStepAdditionalData,
  OnboardingStepDynamicForm,
  OnboardingTab,
  onboardingTabIdPayloadMapper,
  onlyDigitValidator,
  setAllowedData,
  setAllowedFieldOptions,
  setDuplicateErrorsOnJurisdictionInfoTab,
  setDuplicateErrorsOnLicenseInfoTab,
  StateFieldOption,
  ZNATIONAL_JURISDICTION_LABEL,
} from 'components/modal-windows/step-details/onboarding-details/onboarding-details.constant';
import { generateGuid } from 'utils/helpers';
import { BehaviorSubject, forkJoin, NEVER, Observable, of, Subject, Subscriber } from 'rxjs';
import moment from 'moment-mini';
import _size from 'lodash-es/size';
import { WorkflowStep } from 'pages/organization/testing-workflow/objects/workflow-step.object';
import _isEqual from 'lodash-es/isEqual';
import {
  ONBOARDING_LICENSE_REQUIREMENT_OPTION,
  ONBOARDING_LICENSE_TYPES,
  SECTION_TYPE,
} from 'constants/workflow.constants';
import _cloneDeep from 'lodash-es/cloneDeep';

@Component({
  selector: 'cm-onboarding-details',
  styleUrls: ['./onboarding-details.modal.component.scss'],
  templateUrl: './onboarding-details.modal.component.html',
})
export class OnboardingDetailsModalComponent implements OnChanges {
  @Input() step: WorkflowStep;
  @Input() additionalData: OnboardingStepAdditionalData;
  @Output() toggleMode: EventEmitter<string> = new EventEmitter();
  @ViewChild('signature') signatureComponent: SignatureComponent;
  @ViewChild('nav') tabset: NgbNav;
  @ViewChildren(DynamicFormComponent) dynamicFormComponents: DynamicFormComponent[];
  @ViewChild('submitMessage') submitMessage;
  @ViewChild('jurisdictionNextScreenNotification') jurisdictionNextScreenNotification;
  private showSignatureSubject$ = new BehaviorSubject<boolean>(false);
  showSignature$ = this.showSignatureSubject$.asObservable();

  private jurisdictionHelpObject: Partial<{
    dynamicFormInstanceIds: number[];
    subject$: Subject<any>;
    elementOptionsByFieldInstanceId: { [i in number]: { [j in string]: StateFieldOption[] } };
  }> = { elementOptionsByFieldInstanceId: {} };

  formInfo: DynamicFormInfo;
  signature_link: string;
  showErrors = false;
  sections: DynamicFormSection[];
  formErrors: string[] = [];
  modalDetails: DynamicFormContentDetails;
  showAddNationalLink = false;
  private dynamicFormIdOnboardingTabIdMap = {};
  private allowedData: AllowedDataInterface;

  readonly STEP_TYPES = STEP_TYPES;
  readonly ONBOARDING_TAB_TYPES = ONBOARDING_TAB_TYPES;
  readonly ONBOARDING_TAB_ID = ONBOARDING_TAB_ID;
  readonly STEP_TYPES_NAMES = STEP_TYPES_NAMES;
  readonly getRecordIndex = getRecordIndex;
  readonly lastOfVisible = lastOfVisible;

  private selectedCountry: any = {};
  private fieldsIds: Map<string, number> = new Map();
  private firstJumpToLicenseTab = true;

  tabs: OnboardingTab[] = [];

  activeTabId: string = ONBOARDING_TAB_ID.USER_INFORMATION.toString();

  @ComputedFrom('step')
  get isStepSubmitted() {
    return isStepSubmitted(this.step.status_id);
  }

  constructor(
    public activeModal: NgbActiveModal,
    private stepService: StepService,
    private messageQueryService: MessageQueryService,
    private smartModalService: SmartModalService,
    private changeDetector: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes) {
    const { step, additionalData } = changes;
    if (additionalData) {
      this.allowedData = setAllowedData(
        additionalData.currentValue,
        this.step.content.licenseTypeOption === ONBOARDING_LICENSE_TYPES.UNIVERSAL,
      );
    }
    if (step) {
      this.stepInit();
    }
  }

  stepInit() {
    const { description, content, step_id } = this.step;
    const { signature_link, terms, sections } = content;
    this.modalDetails = {
      stepDescription: description,
      disclosureText: terms,
    };
    this.sections = sections;
    this.signature_link = signature_link;

    const obs$ = this.isStepSubmitted ? of(null) : this.stepService.getDynamicFormData(step_id);
    obs$.subscribe((formInfo: DynamicFormInfo) => {
      this.formInfo = formInfo;
      this.tabs = this.setTabsFromContent();
    });
  }

  private setTabsFromContent(): OnboardingTab[] {
    const { content, specialization_label } = this.step;
    const useFormInfo = this.formInfo;
    const tabs: OnboardingTab[] = [];
    const { onboardingForm, sections } = content;
    if (!onboardingForm) {
      return tabs;
    }
    const sectionsWithTooltips = addTooltipToSections(sections, specialization_label, this.isStepSubmitted);

    // userInformation
    const userInformation = onboardingForm.userInformation;
    if (userInformation && userInformation.length) {
      const id = ONBOARDING_TAB_ID.USER_INFORMATION;
      const emptyForm = !useFormInfo
        ? {}
        : {
            ...userInformation[0],
            fields: this.stepService.mapFormInfoFields(
              userInformation[0].fields,
              this.formInfo,
              true,
              additionalMappedStepFields,
            ),
          };
      const forms = userInformation.map((form) => {
        this.dynamicFormIdOnboardingTabIdMap[form.dynamicFormInstanceId] = id;
        return {
          ...form,
          fields: this.setAdditionalParamsToTheFields(
            !useFormInfo
              ? form.fields
              : this.stepService.mapFormInfoFields(
                  form.fields,
                  this.formInfo,
                  true,
                  additionalMappedStepFieldsWithValues,
                ),
            id,
            form.dynamicFormInstanceId,
            specialization_label,
          ),
        };
      });
      tabs.push({
        title: ONBOARDING_TAB_TITLE.USER_INFORMATION,
        id,
        type: ONBOARDING_TAB_TYPES.FORM_GROUP,
        sections: sectionsWithTooltips,
        forms,
        disabled: false,
        emptyForm,
      });
    }
    // jurisdictionInformation
    const jurisdictionInformation = onboardingForm.jurisdictionInformation;
    if (jurisdictionInformation && jurisdictionInformation.length) {
      const id = ONBOARDING_TAB_ID.JURISDICTION_INFORMATION;
      const emptyForm = !useFormInfo
        ? {}
        : {
            ...jurisdictionInformation[0],
            fields: this.stepService.mapFormInfoFields(
              jurisdictionInformation[0].fields,
              this.formInfo,
              true,
              additionalMappedStepFields,
            ),
          };
      const forms = jurisdictionInformation.map((form) => {
        this.dynamicFormIdOnboardingTabIdMap[form.dynamicFormInstanceId] = id;
        return {
          ...form,
          fields: this.setAdditionalParamsToTheFields(
            !useFormInfo
              ? form.fields
              : this.stepService.mapFormInfoFields(
                  form.fields,
                  this.formInfo,
                  true,
                  additionalMappedStepFieldsWithValues,
                ),
            id,
            form.dynamicFormInstanceId,
            specialization_label,
          ),
        };
      });
      tabs.push({
        title: ONBOARDING_TAB_TITLE.JURISDICTION_INFORMATION.replace(/{specialization_label}/g, specialization_label),
        id,
        type: ONBOARDING_TAB_TYPES.FORM_GROUP,
        sections: sectionsWithTooltips,
        forms,
        disabled: !this.isStepSubmitted && !!_size(userInformation),
        emptyForm,
      });
    }
    // licenseInformation
    const licenseInformation = onboardingForm.licenseInformation;
    if (licenseInformation && licenseInformation.length) {
      const id = ONBOARDING_TAB_ID.LICENSE_INFORMATION;
      const emptyForm = !useFormInfo
        ? {}
        : {
            ...licenseInformation[0],
            relatedDynamicFormInstanceId: null,
            fields: setAllowedFieldOptions(
              this.stepService.mapFormInfoFields(
                licenseInformation[0].fields,
                this.formInfo,
                true,
                additionalMappedStepFields,
              ),
              this.allowedData,
            ),
          };
      const forms = licenseInformation.map((form) => {
        this.dynamicFormIdOnboardingTabIdMap[form.dynamicFormInstanceId] = id;
        return {
          ...form,
          fields: this.setAdditionalParamsToTheFields(
            !useFormInfo
              ? form.fields
              : this.stepService.mapFormInfoFields(
                  form.fields,
                  this.formInfo,
                  true,
                  additionalMappedStepFieldsWithValues,
                ),
            id,
            form.dynamicFormInstanceId,
            specialization_label,
          ),
        };
      });
      tabs.push({
        title: ONBOARDING_TAB_TITLE.LICENSE_INFORMATION.replace(/{specialization_label}/g, specialization_label),
        id,
        type: ONBOARDING_TAB_TYPES.FORM_GROUP,
        sections: sectionsWithTooltips,
        forms,
        disabled: !this.isStepSubmitted && !!(_size(userInformation) || _size(jurisdictionInformation)),
        emptyForm,
      });
    }
    // additionalInformation
    const additionalInformation = onboardingForm.additionalInformation;
    if (additionalInformation && additionalInformation.length) {
      const id = ONBOARDING_TAB_ID.ADDITIONAL_INFORMATION;
      const emptyForm = !useFormInfo
        ? {}
        : {
            ...additionalInformation[0],
            fields: this.stepService.mapFormInfoFields(
              additionalInformation[0].fields,
              this.formInfo,
              true,
              additionalMappedStepFields,
            ),
          };
      const forms = additionalInformation.map((form) => {
        this.dynamicFormIdOnboardingTabIdMap[form.dynamicFormInstanceId] = id;
        return {
          ...form,
          fields: this.setAdditionalParamsToTheFields(
            !useFormInfo
              ? form.fields
              : this.stepService.mapFormInfoFields(
                  form.fields,
                  this.formInfo,
                  true,
                  additionalMappedStepFieldsWithValues,
                ),
            id,
            form.dynamicFormInstanceId,
            specialization_label,
          ),
        };
      });
      tabs.push({
        title: ONBOARDING_TAB_TITLE.ADDITIONAL_INFORMATION,
        id,
        type: ONBOARDING_TAB_TYPES.FORM_GROUP,
        sections: sectionsWithTooltips,
        forms,
        disabled:
          !this.isStepSubmitted &&
          !!(_size(userInformation) || _size(jurisdictionInformation) || _size(licenseInformation)),
        emptyForm,
      });
    }
    // signature
    if (
      (useFormInfo && this.formInfo.signature_required) ||
      (this.isStepSubmitted && this.step.content.signature_link)
    ) {
      tabs.push({
        title: ONBOARDING_TAB_TITLE.SIGNATURE,
        id: ONBOARDING_TAB_ID.SIGNATURE,
        type: ONBOARDING_TAB_TYPES.SIGNATURE,
        disabled:
          !this.isStepSubmitted &&
          !!(_size(userInformation) ||
            _size(jurisdictionInformation) ||
            _size(licenseInformation) ||
            _size(additionalInformation)),
      });
    }
    this.activeTabId = tabs[0].id.toString();
    return tabs;
  }

  showTabElements(id: number) {
    if (id === ONBOARDING_TAB_ID.SIGNATURE) {
      this.showSignatureSubject$.next(true);
      this.drawSignature();
    }
  }

  drawSignature() {
    setTimeout(() => {
      if (this.step.content.signature_link) {
        this.signatureComponent.signature = this.step.content.signature_link;
      }
    });
  }

  closeModal() {
    return this.dynamicFormComponents.find((dynamicFormComponent) => {
      return dynamicFormComponent.dynamicForm.dirty;
    })
      ? this.smartModalService
          .openUnsavedChanges()
          .pipe(take(1))
          .subscribe((bool) => bool && this.dismissModal())
      : this.dismissModal();
  }

  back() {
    this.showErrors = false;
    this.formErrors = [];
    const activeTabIndex = this.tabs.findIndex((tab) => tab.id.toString() === this.activeTabId);
    if (this.isFirstTab) {
      return this.dismissModal();
    } else {
      this.tabs[activeTabIndex].disabled = true;
      const previousTab = this.tabs[activeTabIndex - 1];
      this.activeTabId = previousTab.id.toString();
      this.tabset.select(previousTab.id.toString());
    }
  }

  dismissModal() {
    this.activeModal.dismiss();
  }

  nextTab() {
    this.showErrors = false;
    this.formErrors = [];
    const activeTabId = Number(this.activeTabId);
    const dynamicFormComponentsWithActiveTab = this.dynamicFormComponents.filter((form: DynamicFormComponent) => {
      return this.dynamicFormIdOnboardingTabIdMap[form.formId].toString() === this.activeTabId;
    });
    if ([ONBOARDING_TAB_ID.JURISDICTION_INFORMATION, ONBOARDING_TAB_ID.LICENSE_INFORMATION].includes(activeTabId)) {
      clearDuplicateErrorsOnTab(dynamicFormComponentsWithActiveTab);
    }

    let valid = dynamicFormComponentsWithActiveTab.reduce((isValid, form: DynamicFormComponent) => {
      return isValid && !form.dynamicForm.invalid;
    }, true);
    const activeTabIndex = this.tabs.findIndex((tab) => tab.id.toString() === this.activeTabId);
    const nextTab = !this.isLastTab ? this.tabs[activeTabIndex + 1] : null;

    if (
      valid &&
      activeTabId === ONBOARDING_TAB_ID.JURISDICTION_INFORMATION &&
      dynamicFormComponentsWithActiveTab.length > 1
    ) {
      valid = setDuplicateErrorsOnJurisdictionInfoTab(dynamicFormComponentsWithActiveTab);
    }
    if (
      valid &&
      activeTabId === ONBOARDING_TAB_ID.LICENSE_INFORMATION &&
      dynamicFormComponentsWithActiveTab.length > 1
    ) {
      valid = setDuplicateErrorsOnLicenseInfoTab(dynamicFormComponentsWithActiveTab, this.step.specialization_label);
    }

    if (valid) {
      if (!this.isLastTab) {
        const obs$ = [];
        switch (activeTabId) {
          case ONBOARDING_TAB_ID.USER_INFORMATION:
            this.fillJurisdictionsSectionsFromUserInfoTab(dynamicFormComponentsWithActiveTab, obs$);
            break;
          case ONBOARDING_TAB_ID.JURISDICTION_INFORMATION:
            obs$.push(
              new Observable((observer) => {
                (this.firstJumpToLicenseTab
                  ? this.smartModalService.openNotification(
                      {
                        title: `${this.step.specialization_label} INFORMATION – PLEASE READ`.toUpperCase(),
                        body: this.jurisdictionNextScreenNotification,
                        closeButtonText: 'Ok',
                      },
                      {
                        size: 'lg',
                      },
                    )
                  : of(null)
                )
                  .pipe(
                    take(1),
                    tap(() => (this.firstJumpToLicenseTab = false)),
                    switchMap(() => this.addJurisdictionsNationalSections$(dynamicFormComponentsWithActiveTab)),
                  )
                  .subscribe(() => {
                    const dynamicForms = this.dynamicFormComponents.filter((form: DynamicFormComponent) => {
                      return this.dynamicFormIdOnboardingTabIdMap[form.formId].toString() === this.activeTabId;
                    });
                    this.addJurisdictionsSectionsToLicenseInfoTab(dynamicForms, observer);
                    try {
                      this.showAddNationalLink = !!this.allowedData.selectedCountyNames.find(
                        (countryName) => this.allowedData[countryName].zNationalStateIds.length,
                      );
                    } catch (e) {
                      this.showAddNationalLink = false;
                    }
                  });
              }),
            );
            break;
        }
        forkJoin(obs$).subscribe({
          complete: () => {
            nextTab.disabled = false;
            this.activeTabId = nextTab.id.toString();
            this.tabset.select(nextTab.id.toString());
          },
        });
      }
    } else {
      if (!this.isLastTab) {
        nextTab.disabled = true;
      }
      this.showErrors = true;
      this.formErrors = _uniq(
        dynamicFormComponentsWithActiveTab.reduce((result, form: DynamicFormComponent) => {
          return [...result, ...form.getFormErrorMessages()];
        }, []),
      );
    }
  }

  private fillJurisdictionsSectionsFromUserInfoTab(userInformationDynamicFormComponents, obs$: Observable<any>[]) {
    if (userInformationDynamicFormComponents.length !== 1) {
      return;
    }

    const jurisdictionInformationForms = this.dynamicFormComponents.filter((_form: DynamicFormComponent) => {
      return this.dynamicFormIdOnboardingTabIdMap[_form.formId] === ONBOARDING_TAB_ID.JURISDICTION_INFORMATION;
    });
    if (jurisdictionInformationForms.length === 1) {
      const jurisdictionInformationForm = jurisdictionInformationForms[0];
      const {
        elements: jurisdictionInfoElements,
        dynamicForm: { value: jurisdictionInfoValue },
      } = jurisdictionInformationForm;
      const jurisdictionInfoTrackedFieldInstanceIds = {};
      const hasValue = jurisdictionInfoElements.find((element) => {
        const {
          id: _id,
          type: { id: typeId },
          subType,
          fieldInstanceId,
        } = element;
        if (
          typeId === constants.FORM_FIELD_TYPE_IDS.SELECT &&
          [constants.SELECT_SUB_TYPES.COUNTRY, constants.SELECT_SUB_TYPES.STATE].includes(subType)
        ) {
          jurisdictionInfoTrackedFieldInstanceIds[subType] = fieldInstanceId;
          return jurisdictionInfoValue[_id];
        }
        return false;
      });
      if (!hasValue) {
        const {
          elements: userInfoElements,
          dynamicForm: { value: userInfoValue },
        } = userInformationDynamicFormComponents[0];
        const userInfoFormValue = userInfoElements.reduce((_res, element) => {
          const {
            id,
            type: { id: _typeId },
            subType,
            elementOptions,
          } = element;
          if (
            _typeId === constants.FORM_FIELD_TYPE_IDS.SELECT &&
            [constants.SELECT_SUB_TYPES.COUNTRY, constants.SELECT_SUB_TYPES.STATE].includes(subType)
          ) {
            const option = elementOptions.find((_option) => _option.id === userInfoValue[id]);
            if (option) {
              _res[subType] = subType === constants.SELECT_SUB_TYPES.COUNTRY ? option.name : option.stateId;
            }
          }
          return _res;
        }, {});
        if (
          !userInfoFormValue[constants.SELECT_SUB_TYPES.COUNTRY] &&
          this.allowedData.allowedCountryNames.length !== 1
        ) {
          return;
        }
        if (!this.allowedData.allowedCountryNames.includes(userInfoFormValue[constants.SELECT_SUB_TYPES.COUNTRY])) {
          if (this.allowedData.allowedCountryNames.length === 1) {
            userInfoFormValue[constants.SELECT_SUB_TYPES.COUNTRY] = this.allowedData.allowedCountryNames[0];
            userInfoFormValue[constants.SELECT_SUB_TYPES.STATE] =
              this.allowedData[userInfoFormValue[constants.SELECT_SUB_TYPES.COUNTRY]].allowedStateIds.length === 1
                ? this.allowedData[userInfoFormValue[constants.SELECT_SUB_TYPES.COUNTRY]].allowedStateIds[0]
                : null;
          } else {
            return;
          }
        }
        if (
          !this.allowedData[userInfoFormValue[constants.SELECT_SUB_TYPES.COUNTRY]].allowedStateIds.includes(
            userInfoFormValue[constants.SELECT_SUB_TYPES.STATE],
          )
        ) {
          if (this.allowedData[userInfoFormValue[constants.SELECT_SUB_TYPES.COUNTRY]].allowedStateIds.length === 1) {
            userInfoFormValue[constants.SELECT_SUB_TYPES.STATE] =
              this.allowedData[userInfoFormValue[constants.SELECT_SUB_TYPES.COUNTRY]].allowedStateIds[0];
          } else {
            delete userInfoFormValue[constants.SELECT_SUB_TYPES.STATE];
          }
        }
        const subj$ = new Subject();
        obs$.push(subj$.asObservable());

        const stateFieldInstanceId = jurisdictionInfoTrackedFieldInstanceIds[constants.SELECT_SUB_TYPES.STATE];
        const countryName = userInfoFormValue[constants.SELECT_SUB_TYPES.COUNTRY];

        this.stepService
          .getFieldInstanceOptions(stateFieldInstanceId, {
            countryName,
          })
          .subscribe((response) => {
            const elementOptionsUnfiltered = response.map(({ optionId, name, stateId }) => ({
              id: optionId,
              name,
              stateId,
            }));
            if (!this.jurisdictionHelpObject.elementOptionsByFieldInstanceId[stateFieldInstanceId]) {
              this.jurisdictionHelpObject.elementOptionsByFieldInstanceId[stateFieldInstanceId] = {};
            }
            this.jurisdictionHelpObject.elementOptionsByFieldInstanceId[stateFieldInstanceId][countryName] =
              elementOptionsUnfiltered;
            const elementOptions = _cloneDeep(elementOptionsUnfiltered);

            const jurisdictionInfoTab = this.tabs.find(({ id }) => id === ONBOARDING_TAB_ID.JURISDICTION_INFORMATION);

            const fields = jurisdictionInfoTab.emptyForm.fields.map((field) => {
              const {
                type: { id: typeId },
                subType,
                elementOptions: _elementOptions,
              } = field;
              if (
                typeId === constants.FORM_FIELD_TYPE_IDS.SELECT &&
                [constants.SELECT_SUB_TYPES.COUNTRY, constants.SELECT_SUB_TYPES.STATE].includes(subType)
              ) {
                const newElementOptions =
                  subType === constants.SELECT_SUB_TYPES.COUNTRY
                    ? _elementOptions
                    : elementOptions.filter(({ stateId }) =>
                        this.allowedData[
                          userInfoFormValue[constants.SELECT_SUB_TYPES.COUNTRY]
                        ].allowedStateIds.includes(stateId),
                      );
                const option = newElementOptions.find((_option: DynamicFormFieldOption) => {
                  if (subType === constants.SELECT_SUB_TYPES.COUNTRY) {
                    return (_option.value || _option.name) === userInfoFormValue[subType];
                  } else {
                    return _option.stateId === userInfoFormValue[subType];
                  }
                });
                const idValue = option ? option.id : null;
                if (subType === constants.SELECT_SUB_TYPES.COUNTRY) {
                  this.allowedData[userInfoFormValue[subType]].selected = true;
                } else {
                  this.allowedData[userInfoFormValue[constants.SELECT_SUB_TYPES.COUNTRY]].selectedStateIds.push(
                    userInfoFormValue[subType],
                  );
                }

                return {
                  ...field,
                  elementOptions: newElementOptions,
                  ignoreIsPushInitValue: true,
                  value: idValue,
                };
              }
              return field;
            });

            const jurisdictionInfoTabForm = jurisdictionInfoTab.forms[0];
            jurisdictionInfoTab.forms[0] = {
              ...jurisdictionInfoTabForm,
              fields: this.setAdditionalParamsToTheFields(
                this.stepService.mapFormInfoFields(fields, this.formInfo, true, additionalMappedStepFieldsWithValues),
                ONBOARDING_TAB_ID.JURISDICTION_INFORMATION,
                jurisdictionInfoTabForm.dynamicFormInstanceId,
                this.step.specialization_label,
              ),
            };
            subj$.complete();
          });
      }
    }
  }

  private addJurisdictionsNationalSections$(dynamicFormComponents): Observable<unknown> {
    return new Observable((obs$) => {
      const tabForms = this.tabs.find(({ id }) => id === ONBOARDING_TAB_ID.JURISDICTION_INFORMATION).forms;
      const selectedCountryNamesMap: { [key in string]: number } = {};
      const addedNationalSections = {};
      dynamicFormComponents.forEach((form: DynamicFormComponent) => {
        const {
          elements,
          formId,
          dynamicForm: { value },
        } = form;
        const { isNational } = tabForms.find(({ dynamicFormInstanceId }) => dynamicFormInstanceId === formId);
        let countryNameValue;
        let countryValue;

        if (!isNational) {
          const { id, elementOptions } = elements.find((element) => {
            const {
              type: { id: typeId },
              subType,
            } = element;
            return typeId === constants.FORM_FIELD_TYPE_IDS.SELECT && subType === constants.SELECT_SUB_TYPES.COUNTRY;
          });
          countryValue = value[id];
          countryNameValue = elementOptions.find(({ id: _id }) => _id === countryValue).name;
          selectedCountryNamesMap[countryNameValue] = countryValue;
        } else {
          elements.forEach((element) => {
            const {
              id,
              type: { id: typeId },
              subType,
              elementOptions,
            } = element;
            if (typeId === constants.FORM_FIELD_TYPE_IDS.SELECT) {
              switch (subType) {
                case constants.SELECT_SUB_TYPES.COUNTRY:
                  if (!addedNationalSections[value[id]]) {
                    addedNationalSections[value[id]] = {
                      formIds: [],
                      stateIds: [],
                    };
                  }
                  addedNationalSections[value[id]].formIds.push(formId);
                  countryValue = value[id];
                  break;
                case constants.SELECT_SUB_TYPES.STATE:
                  const stateValue = value[id];
                  const stateId = elementOptions.find(({ id: _id }) => _id === stateValue).stateId;
                  if (!countryNameValue) {
                    const country = elements.find((_element) => {
                      const {
                        type: { id: _typeId },
                        subType: _subType,
                      } = _element;
                      return (
                        _typeId === constants.FORM_FIELD_TYPE_IDS.SELECT &&
                        _subType === constants.SELECT_SUB_TYPES.COUNTRY
                      );
                    });
                    countryValue = value[country.id];
                    if (!addedNationalSections[countryValue]) {
                      addedNationalSections[countryValue] = {
                        formIds: [],
                        stateIds: [],
                      };
                    }
                  }
                  addedNationalSections[countryValue].stateIds.push(stateId);
                  break;
              }
            }
          });
        }
      });
      Object.keys(addedNationalSections).forEach((countryValue) => {
        const value = Number(countryValue);
        const selectedCountryValues = Object.values(selectedCountryNamesMap);
        if (!selectedCountryValues.includes(value)) {
          addedNationalSections[countryValue].formIds.forEach((formId) => {
            this.removeForm(ONBOARDING_TAB_ID.JURISDICTION_INFORMATION, formId, true);
          });
        }
      });

      const actionTab = this.tabs.find(({ id }) => id === ONBOARDING_TAB_ID.JURISDICTION_INFORMATION);
      Object.keys(selectedCountryNamesMap).forEach((countryName) => {
        const countryValue = selectedCountryNamesMap[countryName];
        this.allowedData[countryName].zNationalStateIds.forEach((_stateId) => {
          if (
            !addedNationalSections[countryValue] ||
            !_size(addedNationalSections[countryValue].stateIds) ||
            !addedNationalSections[countryValue].stateIds.includes(_stateId)
          ) {
            const formFields = actionTab.emptyForm.fields.map((field) => {
              const {
                type: { id: _typeId },
                subType,
                fieldInstanceId,
              } = field;
              if (_typeId === constants.FORM_FIELD_TYPE_IDS.SELECT) {
                switch (subType) {
                  case constants.SELECT_SUB_TYPES.COUNTRY:
                    return {
                      ...field,
                      ignoreIsPushInitValue: true,
                      value: countryValue,
                    };
                  case constants.SELECT_SUB_TYPES.STATE:
                    const elementOptions =
                      this.jurisdictionHelpObject.elementOptionsByFieldInstanceId[fieldInstanceId][countryName];
                    const stateOption = elementOptions.find(({ stateId }) => stateId === _stateId);
                    return {
                      ...field,
                      elementOptions,
                      ignoreIsPushInitValue: true,
                      value: stateOption.id,
                    };
                }
              }
              return field;
            });
            this.addAnotherForm(ONBOARDING_TAB_ID.JURISDICTION_INFORMATION, {
              isNational: true,
              formFields,
            });
          }
        });
      });
      setTimeout(() => {
        obs$.next();
        obs$.complete();
      });
    });
  }

  private addJurisdictionsSectionsToLicenseInfoTab(dynamicFormComponents, obs$: Subscriber<unknown>) {
    const tabForms = this.tabs.find(({ id }) => id === ONBOARDING_TAB_ID.JURISDICTION_INFORMATION).forms;
    /* jurisdictionFormSelectedOptions example [
    {
      "2": {
        "value": "United States",
        "id": 73299142,
        "licenseTypeId": null,
        "name": "United States"
      },
      "3": {
        "id": 73299146,
        "name": "Alabama",
        "stateId": 76
      },
      "formId": 63871111,
      "isNational": true,
    },
  ] */
    const jurisdictionFormSelectedOptions = dynamicFormComponents.reduce((res, form: DynamicFormComponent) => {
      const {
        elements,
        formId,
        dynamicForm: { value },
      } = form;
      const { isNational } = tabForms.find(({ dynamicFormInstanceId }) => dynamicFormInstanceId === formId);
      const formValue = elements.reduce(
        (_res, element) => {
          const {
            id,
            type: { id: _typeId },
            subType,
            elementOptions,
          } = element;
          if (
            _typeId === constants.FORM_FIELD_TYPE_IDS.SELECT &&
            [constants.SELECT_SUB_TYPES.COUNTRY, constants.SELECT_SUB_TYPES.STATE].includes(subType)
          ) {
            _res[subType] = elementOptions.find((_option) => _option.id === value[id]);
          }
          return _res;
        },
        { formId, isNational },
      );
      return [...res, formValue];
    }, []);
    this.allowedData.selectedCountyNames = [];
    const alreadyPassedCountryNames = [];
    /* jurisdictionFormValues example [
  {
    "2": "United States",
    "3": 76
  },
]*/
    const jurisdictionFormValues = jurisdictionFormSelectedOptions.map((options) => {
      const {
        [constants.SELECT_SUB_TYPES.COUNTRY]: countryOption,
        [constants.SELECT_SUB_TYPES.STATE]: stateOption,
        formId,
        isNational,
      } = options;
      const countryName = countryOption.name;
      const { stateId } = stateOption;
      try {
        if (!alreadyPassedCountryNames.includes(countryName)) {
          alreadyPassedCountryNames.push(countryName);
          this.allowedData[countryName].selectedStateIds = [];
        }
        this.allowedData.selectedCountyNames.push(countryName);
        this.allowedData[countryName].selected = true;
        this.allowedData[countryName].selectedStateIds.push(stateId);
        this.allowedData.allowedLicenseTypesByStateId[stateId].dynamicFormInstanceId = formId;
        this.allowedData.allowedLicenseTypesByStateId[stateId].isNational = isNational;
      } catch (e) {}
      return {
        [constants.SELECT_SUB_TYPES.COUNTRY]: countryName,
        [constants.SELECT_SUB_TYPES.STATE]: stateId,
      };
    });
    const licenseInformationForms = this.dynamicFormComponents.filter((_form: DynamicFormComponent) => {
      return this.dynamicFormIdOnboardingTabIdMap[_form.formId] === ONBOARDING_TAB_ID.LICENSE_INFORMATION;
    });
    const licenseInformationTab = this.tabs.find(({ id }) => id === ONBOARDING_TAB_ID.LICENSE_INFORMATION);
    if (!licenseInformationForms.length) {
      licenseInformationForms.push({
        elements: licenseInformationTab.emptyForm.fields,
        dynamicForm: { getRawValue: () => ({}) },
      } as DynamicFormComponent);
    }
    const licenseInformationTrackedFieldInstanceIds = {};
    /* stateValuesMap example
    * {
  "73298923": {
    "formId": 63871126,
    "disabled": true
  }
}*/
    const stateValuesMap = {};
    const licenseInformationValues = licenseInformationForms.reduce((res, form: DynamicFormComponent) => {
      const { elements, dynamicForm } = form;
      const value = dynamicForm.getRawValue();
      const formId = form.formId;
      const formValue = elements.reduce((_res, element) => {
        const {
          id,
          type: { id: _typeId },
          subType,
          elementOptions,
          fieldInstanceId,
          value: elementValue,
          disabled,
          sectionTypeId,
        } = element;
        if (
          _typeId === constants.FORM_FIELD_TYPE_IDS.SELECT &&
          sectionTypeId !== SECTION_TYPE.LICENSE_ADDRESS_INFORMATION
        ) {
          const option = elementOptions.find((_option) => _option.id === value[id]);
          if (
            [
              constants.SELECT_SUB_TYPES.COUNTRY,
              constants.SELECT_SUB_TYPES.STATE,
              constants.SELECT_SUB_TYPES.LICENSE_TYPE,
            ].includes(subType)
          ) {
            licenseInformationTrackedFieldInstanceIds[subType] = fieldInstanceId;
          }
          try {
            switch (subType) {
              case constants.SELECT_SUB_TYPES.COUNTRY:
                _res[subType] = option.name;
                break;
              case constants.SELECT_SUB_TYPES.STATE:
                _res[subType] = option.stateId;
                const stateValue = elementValue as number;
                if (stateValue) {
                  if (!stateValuesMap[stateValue] || disabled) {
                    stateValuesMap[stateValue] = {
                      formId,
                      disabled,
                      stateId: option.stateId,
                    };
                  }
                }
                break;
            }
          } catch (e) {}
        }
        return _res;
      }, {});
      return [...res, formValue];
    }, []);
    const existValues = [];
    licenseInformationValues.forEach((licenseInformationValue) => {
      jurisdictionFormValues.forEach((value) => {
        if (_isEqual(value, licenseInformationValue)) {
          licenseInformationValue.valid = true;
          existValues.push(value);
        }
      });
    });
    const removeIndexes = licenseInformationValues.reduce((res, v, i) => {
      if (!v.valid) {
        res.push(i);
      }
      return res;
    }, []);
    if (removeIndexes.length) {
      licenseInformationTab.forms = licenseInformationTab.forms.filter((_, i) => !removeIndexes.includes(i));
    }
    if (licenseInformationTab.forms.length) {
      // for saved forms
      Object.values(stateValuesMap).forEach((value: any) => {
        const { disabled, formId, stateId } = value;
        if (!disabled) {
          const dynamicFormElement = licenseInformationForms.find(({ formId: _formId }) => formId === _formId);
          const { isNational } = this.allowedData.allowedLicenseTypesByStateId[stateId];
          if (!dynamicFormElement) {
            return;
          }
          const {
            elements,
            dynamicForm: { controls },
          } = dynamicFormElement;
          elements.forEach((element) => {
            const {
              id: _id,
              type: { id: _typeId },
              subType,
              sectionTypeId,
            } = element;
            if (
              !isNational &&
              _typeId === constants.FORM_FIELD_TYPE_IDS.SELECT &&
              sectionTypeId !== SECTION_TYPE.LICENSE_ADDRESS_INFORMATION &&
              [constants.SELECT_SUB_TYPES.COUNTRY, constants.SELECT_SUB_TYPES.STATE].includes(subType)
            ) {
              element.disabled = true;
              controls[_id].disable({ emitEvent: false });
              const form = licenseInformationTab.forms.find(
                ({ dynamicFormInstanceId }) => dynamicFormInstanceId === formId,
              );
              if (form) {
                form.required = true;
              }
            }
            if (
              isNational &&
              _typeId === constants.FORM_FIELD_TYPE_IDS.SELECT &&
              subType === constants.SELECT_SUB_TYPES.STATE &&
              sectionTypeId !== SECTION_TYPE.LICENSE_ADDRESS_INFORMATION
            ) {
              element.label = ZNATIONAL_JURISDICTION_LABEL;
            }
          });
        }
      });
    }
    const newFormValues = jurisdictionFormValues.filter((v) => !existValues.includes(v));
    if (newFormValues.length) {
      const countryNames = _uniq(newFormValues.map((value) => value[constants.SELECT_SUB_TYPES.COUNTRY])) as any;

      const statesObs$ = countryNames.map((name) =>
        this.stepService.getFieldInstanceOptions(
          licenseInformationTrackedFieldInstanceIds[constants.SELECT_SUB_TYPES.STATE],
          {
            countryName: name,
          },
        ),
      );
      forkJoin(statesObs$).subscribe((responses) => {
        const countyStateOptionsMap = (responses as any).reduce((res, response: any[], i) => {
          res[countryNames[i]] = response.map(({ optionId, name, stateId }) => ({
            id: optionId,
            name,
            stateId,
          }));
          return res;
        }, {});

        const stateIds = [];
        let newFormFields = [];

        newFormValues.forEach((value) => {
          let stateValue;
          const newFields = licenseInformationTab.emptyForm.fields.map((field) => {
            const {
              type: { id: _typeId },
              subType,
              sectionTypeId,
            } = field;
            if (
              _typeId === constants.FORM_FIELD_TYPE_IDS.SELECT &&
              sectionTypeId !== SECTION_TYPE.LICENSE_ADDRESS_INFORMATION &&
              [constants.SELECT_SUB_TYPES.COUNTRY, constants.SELECT_SUB_TYPES.STATE].includes(subType)
            ) {
              let option;
              const changedParams = {
                value: field.value,
                elementOptions: field.elementOptions,
                ignoreIsPushInitValue: false,
                disabled: true,
              };
              switch (subType) {
                case constants.SELECT_SUB_TYPES.COUNTRY:
                  option = field.elementOptions.find((_option) => (_option.name || _option.value) === value[subType]);
                  changedParams.value = option.id;
                  changedParams.ignoreIsPushInitValue = true;
                  break;
                case constants.SELECT_SUB_TYPES.STATE:
                  changedParams.elementOptions = countyStateOptionsMap[value[constants.SELECT_SUB_TYPES.COUNTRY]];
                  option = changedParams.elementOptions.find((_option) => _option.stateId === value[subType]);
                  stateIds.push(value[subType]);
                  stateValue = value[subType];
                  changedParams.value = option.id;
                  break;
              }
              return {
                ...field,
                ...changedParams,
              };
            }
            return field;
          }) as DynamicFormField[];
          if (
            this.step.content.licenseRequirementOption !== ONBOARDING_LICENSE_REQUIREMENT_OPTION.ANY_LICENSE ||
            !stateValue ||
            !this.allowedData.allowedLicenseTypesByStateId[stateValue].isNational
          ) {
            newFormFields.push({
              fields: newFields,
              required: true,
            });
          }
        });
        if (
          stateIds.length &&
          this.step.content.licenseRequirementOption !== ONBOARDING_LICENSE_REQUIREMENT_OPTION.ANY_LICENSE
        ) {
          const licenseTypes = stateIds.map((stateId) => this.allowedData.allowedLicenseTypesByStateId[stateId]);
          newFormFields = licenseTypes.reduce((res, response, i) => {
            const { allowed, required, dynamicFormInstanceId, isNational } = response;
            const { fields: formFields } = newFormFields[i];
            let newForms = required.map((option) => {
              const newForm = formFields.map((field) => {
                const {
                  type: { id: _typeId },
                  subType,
                  sectionTypeId,
                } = field;
                if (
                  _typeId === constants.FORM_FIELD_TYPE_IDS.SELECT &&
                  sectionTypeId !== SECTION_TYPE.LICENSE_ADDRESS_INFORMATION
                ) {
                  if ([constants.SELECT_SUB_TYPES.COUNTRY, constants.SELECT_SUB_TYPES.STATE].includes(subType)) {
                    const label =
                      isNational && subType === constants.SELECT_SUB_TYPES.STATE
                        ? ZNATIONAL_JURISDICTION_LABEL
                        : field.label;
                    return {
                      ...field,
                      label,
                      ignoreIsPushInitValue: true,
                      disabled: true,
                    };
                  }
                  if (subType === constants.SELECT_SUB_TYPES.LICENSE_TYPE) {
                    return {
                      ...field,
                      elementOptions: required,
                      value: option.id,
                      disabled: true,
                    };
                  }
                }
                return field;
              });
              return {
                fields: newForm,
                formId: dynamicFormInstanceId,
                isNational,
                required: true,
              };
            });
            if (!newForms.length && !isNational) {
              const noLicenseTypeForm = formFields.map((field) => {
                const {
                  type: { id: _typeId },
                  subType,
                  sectionTypeId,
                } = field;
                if (
                  _typeId === constants.FORM_FIELD_TYPE_IDS.SELECT &&
                  sectionTypeId !== SECTION_TYPE.LICENSE_ADDRESS_INFORMATION
                ) {
                  if ([constants.SELECT_SUB_TYPES.COUNTRY, constants.SELECT_SUB_TYPES.STATE].includes(subType)) {
                    return {
                      ...field,
                      ignoreIsPushInitValue: true,
                    };
                  }
                  if (subType === constants.SELECT_SUB_TYPES.LICENSE_TYPE) {
                    return {
                      ...field,
                      elementOptions: allowed,
                    };
                  }
                }
                return field;
              });
              newForms = [
                {
                  fields: noLicenseTypeForm,
                  formId: dynamicFormInstanceId,
                  required: true,
                  isNational: false,
                },
              ];
            }
            return [...res, ...newForms];
          }, []);
        }
        if (!newFormFields.length && !licenseInformationTab.forms.length) {
          newFormFields.push({
            fields: licenseInformationTab.emptyForm.fields,
          });
        }
        const fullForms = newFormFields.map(({ formId, fields, required, isNational }) => {
          const dynamicFormInstanceId = generateGuid();
          this.dynamicFormIdOnboardingTabIdMap[dynamicFormInstanceId] = ONBOARDING_TAB_ID.LICENSE_INFORMATION;
          return {
            ...licenseInformationTab.emptyForm,
            fields: this.setAdditionalParamsToTheFields(
              this.stepService.mapFormInfoFields(
                _cloneDeep(fields),
                this.formInfo,
                true,
                additionalMappedStepFieldsWithValues,
              ),
              licenseInformationTab.id,
              dynamicFormInstanceId,
              this.step.specialization_label,
            ),
            relatedDynamicFormInstanceId: formId,
            isNational,
            dynamicFormInstanceId,
            required,
          };
        });
        licenseInformationTab.forms = [...licenseInformationTab.forms, ...fullForms];
        obs$.complete();
      });
    } else {
      obs$.complete();
    }
  }

  addAnotherForm(tabId: ONBOARDING_TAB_ID, configuredParams: any = { isNational: false }) {
    const actionTab = this.tabs.find(({ id }) => id === tabId);
    const { isNational, formFields } = configuredParams;
    const dynamicFormInstanceId = generateGuid();
    this.dynamicFormIdOnboardingTabIdMap[dynamicFormInstanceId] = tabId;
    let emptyFormFields = formFields || actionTab.emptyForm.fields;
    if (!formFields) {
      switch (tabId) {
        case ONBOARDING_TAB_ID.JURISDICTION_INFORMATION:
          emptyFormFields = emptyFormFields.map((field) => {
            const {
              type: { id: _typeId },
              subType,
              elementOptions,
            } = field;
            if (_typeId === constants.FORM_FIELD_TYPE_IDS.SELECT) {
              if (this.allowedData.allowedCountryNames.length === 1) {
                if (subType === constants.SELECT_SUB_TYPES.COUNTRY) {
                  const option = elementOptions.find(({ value }) => value === this.allowedData.allowedCountryNames[0]);
                  if (option) {
                    return {
                      ...field,
                      value: option.id,
                    };
                  }
                }
              }
            }
            return field;
          });
          break;
        case ONBOARDING_TAB_ID.LICENSE_INFORMATION:
          emptyFormFields = emptyFormFields.map((field) => {
            const {
              type: { id: _typeId },
              subType,
              elementOptions,
              sectionTypeId,
            } = field;
            if (
              _typeId === constants.FORM_FIELD_TYPE_IDS.SELECT &&
              subType === constants.SELECT_SUB_TYPES.COUNTRY &&
              sectionTypeId !== SECTION_TYPE.LICENSE_ADDRESS_INFORMATION
            ) {
              return {
                ...field,
                elementOptions: elementOptions.filter(({ name, value }) =>
                  this.allowedData.selectedCountyNames.includes((name || value) as string),
                ),
              };
            }
            if (
              isNational &&
              _typeId === constants.FORM_FIELD_TYPE_IDS.SELECT &&
              subType === constants.SELECT_SUB_TYPES.STATE &&
              sectionTypeId !== SECTION_TYPE.LICENSE_ADDRESS_INFORMATION
            ) {
              return {
                ...field,
                label: ZNATIONAL_JURISDICTION_LABEL,
              };
            }
            return field;
          });
          break;
      }
    }
    const newForm = {
      ...actionTab.emptyForm,
      isNational,
      fields: this.setAdditionalParamsToTheFields(
        this.stepService.mapFormInfoFields(emptyFormFields, this.formInfo, true, additionalMappedStepFieldsWithValues),
        actionTab.id,
        dynamicFormInstanceId,
        this.step.specialization_label,
      ),
      dynamicFormInstanceId,
    };
    actionTab.forms = [...actionTab.forms, newForm];
  }

  removeForm(tabId: ONBOARDING_TAB_ID, removedDynamicFormInstanceId: number, silent = false) {
    const tab = this.tabs.find(({ id }) => id === tabId);
    if (tab.forms.length < 2) {
      return;
    }
    const licenseInfoTab = this.tabs.find(({ id: _id }) => _id === ONBOARDING_TAB_ID.LICENSE_INFORMATION);
    const changeCountryStateOnJurisdictionTab =
      tabId === ONBOARDING_TAB_ID.JURISDICTION_INFORMATION &&
      !!licenseInfoTab.forms.find(
        ({ relatedDynamicFormInstanceId }) => relatedDynamicFormInstanceId === removedDynamicFormInstanceId,
      );
    let obs$;
    if (silent) {
      obs$ = of(true);
    } else {
      const entryLabel =
        tabId === ONBOARDING_TAB_ID.JURISDICTION_INFORMATION ? 'country/state' : this.step.specialization_label;
      obs$ = changeCountryStateOnJurisdictionTab
        ? this.changeCountryStateConfirmWindow$()
        : this.removeEntryConfirmWindow$(entryLabel);
    }
    obs$.pipe(filter((confirm) => !!confirm)).subscribe(() => {
      if (changeCountryStateOnJurisdictionTab) {
        licenseInfoTab.forms = licenseInfoTab.forms.filter(
          ({ relatedDynamicFormInstanceId }) => relatedDynamicFormInstanceId !== removedDynamicFormInstanceId,
        );
        if (!licenseInfoTab.forms.length) {
          this.addAnotherForm(ONBOARDING_TAB_ID.LICENSE_INFORMATION);
        }
      }
      tab.forms = tab.forms.filter(
        ({ dynamicFormInstanceId }) => dynamicFormInstanceId !== removedDynamicFormInstanceId,
      );
    });
  }

  private removeEntryConfirmWindow$(entryLabel: string) {
    return this.smartModalService
      .openConfirm({
        title: 'Confirm your action',
        body: `Are you sure you would like to remove this ${entryLabel}?`,
        successButtonText: 'Confirm',
      })
      .pipe(take(1));
  }

  submit(saveAsDraft = false) {
    const signatureErrorsMessage = [];
    this.showErrors = false;
    this.formErrors = [];

    const activeTabId = Number(this.activeTabId);
    const dynamicFormComponentsWithActiveTab = this.dynamicFormComponents.filter((form: DynamicFormComponent) => {
      return this.dynamicFormIdOnboardingTabIdMap[form.formId].toString() === this.activeTabId;
    });

    if ([ONBOARDING_TAB_ID.JURISDICTION_INFORMATION, ONBOARDING_TAB_ID.LICENSE_INFORMATION].includes(activeTabId)) {
      clearDuplicateErrorsOnTab(dynamicFormComponentsWithActiveTab);
    }

    // forms validation
    const invalidTabId = saveAsDraft
      ? []
      : _uniq(
          this.dynamicFormComponents.reduce((result, form: DynamicFormComponent) => {
            if (form.dynamicForm.invalid) {
              result.push(this.dynamicFormIdOnboardingTabIdMap[form.formId]);
            }
            return result;
          }, []),
        );

    let valid = !invalidTabId.length;

    if (
      valid &&
      !saveAsDraft &&
      activeTabId === ONBOARDING_TAB_ID.LICENSE_INFORMATION &&
      dynamicFormComponentsWithActiveTab.length > 1
    ) {
      valid = setDuplicateErrorsOnLicenseInfoTab(dynamicFormComponentsWithActiveTab, this.step.specialization_label);
    }

    // signature validation
    if (this.signatureComponent) {
      const stepSignatureError = this.signatureComponent.getSignatureErrors();

      if (stepSignatureError) {
        if (saveAsDraft) {
          this.signature_link = '';
        } else {
          signatureErrorsMessage.push(stepSignatureError);
        }
      } else {
        this.signature_link = this.signatureComponent.signature;
      }
    }

    if (valid && !signatureErrorsMessage.length) {
      const payload = this.dynamicFormComponents.reduce((_payload, form: DynamicFormComponent) => {
        const { formId, dynamicForm } = form;
        const tabId = this.dynamicFormIdOnboardingTabIdMap[formId];
        const payloadParam = onboardingTabIdPayloadMapper[tabId];
        const tab = this.tabs.find(({ id }) => id === tabId);
        const { relatedDynamicFormInstanceId } = tab.forms.find(
          ({ dynamicFormInstanceId }) => dynamicFormInstanceId === formId,
        );
        if (!_payload[payloadParam]) {
          _payload[payloadParam] = [];
        }
        _payload[payloadParam].push({
          dynamicFormInstanceId: formId,
          relatedDynamicFormInstanceId,
          formValue: dynamicForm.getRawValue(),
        });
        return _payload;
      }, {});
      return this.completeStep(payload, saveAsDraft);
    } else {
      this.showErrors = true;
      this.formErrors = _uniq(
        this.dynamicFormComponents.reduce((result, form: DynamicFormComponent) => {
          return [...result, ...form.getFormErrorMessages()];
        }, []),
      );
      if (signatureErrorsMessage.length) {
        this.formErrors.push(_uniq(signatureErrorsMessage) as any);
      }
    }
  }

  onTabChange(event) {
    if (!this.isStepSubmitted) {
      event.preventDefault();
    } else {
      this.activeTabId = event.nextId;
    }
  }

  private setAdditionalParamsToTheFields(
    fields,
    tabId: ONBOARDING_TAB_ID,
    dynamicFormInstanceId: number,
    specialization_label = '',
  ) {
    const tooltipClass = 'max-400 text-left';
    if (!fields.length) {
      return fields;
    }
    if (fields.length) {
      return fields.map((field) => {
        if (field.type.id === constants.FORM_FIELD_TYPE_IDS.SELECT) {
          if (field.subType === constants.SELECT_SUB_TYPES.COUNTRY) {
            this.setFieldIdMap(
              `${ONBOARDING_FIELD_IDS_MAP.COUNTRY}${tabId}${field.sectionId}${dynamicFormInstanceId}`,
              field.id,
            );
            if (!this.selectedCountry[tabId]) {
              this.selectedCountry[tabId] = {};
            }
            if (!this.selectedCountry[tabId][field.sectionId]) {
              this.selectedCountry[tabId][field.sectionId] = {};
            }
            if (!this.selectedCountry[tabId][field.sectionId][dynamicFormInstanceId]) {
              this.selectedCountry[tabId][field.sectionId][dynamicFormInstanceId] = field.value
                ? field.elementOptions.find(({ id: elementOption }) => elementOption === Number(field.value))
                : {};
            } else {
              const state = this.selectedCountry[tabId][field.sectionId][dynamicFormInstanceId].state;
              this.selectedCountry[tabId][field.sectionId][dynamicFormInstanceId] = {
                ...(field.value
                  ? field.elementOptions.find(({ id: elementOption }) => elementOption === Number(field.value))
                  : {}),
                state,
              };
            }
            let elementOptions = field.elementOptions;
            if (
              [ONBOARDING_TAB_ID.JURISDICTION_INFORMATION, ONBOARDING_TAB_ID.LICENSE_INFORMATION].includes(tabId) &&
              field.sectionTypeId !== SECTION_TYPE.LICENSE_ADDRESS_INFORMATION
            ) {
              elementOptions = elementOptions.filter(({ name, value }) => {
                const stringValue = name || value;
                return this.allowedData.allowedCountryNames.includes(stringValue);
              });
            }

            return {
              ...field,
              elementOptions,
              isWatchChangeValue: !!field.value || !this.isStepSubmitted,
              isPushInitValue: !field.ignoreIsPushInitValue && !!field.value && !this.isStepSubmitted,
            };
          }
          if (field.subType === constants.SELECT_SUB_TYPES.STATE) {
            this.setFieldIdMap(
              `${ONBOARDING_FIELD_IDS_MAP.STATE}${tabId}${field.sectionId}${dynamicFormInstanceId}`,
              field.id,
            );
            const country = fields.find(
              ({ type, subType, sectionId }) =>
                sectionId === field.sectionId &&
                type &&
                type.id === constants.FORM_FIELD_TYPE_IDS.SELECT &&
                subType === constants.SELECT_SUB_TYPES.COUNTRY,
            );
            if (!this.selectedCountry[tabId]) {
              this.selectedCountry[tabId] = {};
            }
            if (!this.selectedCountry[tabId][field.sectionId]) {
              this.selectedCountry[tabId][field.sectionId] = {};
            }
            if (!this.selectedCountry[tabId][field.sectionId][dynamicFormInstanceId]) {
              this.selectedCountry[tabId][field.sectionId][dynamicFormInstanceId] = {
                state: field.value,
              };
            } else {
              this.selectedCountry[tabId][field.sectionId][dynamicFormInstanceId].state = field.value;
            }
            return {
              ...field,
              filledByDisabled: (!country || !country.value) && !field.value && !this.isStepSubmitted,
              isWatchChangeValue: !!field.value || !this.isStepSubmitted,
            };
          }
          if (field.subType === constants.SELECT_SUB_TYPES.LICENSE_TYPE) {
            this.setFieldIdMap(
              `${ONBOARDING_FIELD_IDS_MAP.LICENSE_TYPE}${tabId}${field.sectionId}${dynamicFormInstanceId}`,
              field.id,
            );
            const state = fields.find(
              ({ type, subType, sectionId }) =>
                sectionId === field.sectionId &&
                type &&
                type.id === constants.FORM_FIELD_TYPE_IDS.SELECT &&
                subType === constants.SELECT_SUB_TYPES.STATE,
            );
            return {
              ...field,
              filledByDisabled: (!state || !state.value) && !field.value && !this.isStepSubmitted,
            };
          }
        }
        if (field.type.id === constants.FORM_FIELD_TYPE_IDS.TEXT) {
          if (field.subType === constants.TEXT_SUB_TYPES.ZIP_CODE) {
            this.setFieldIdMap(
              `${ONBOARDING_FIELD_IDS_MAP.ZIP_CODE}${tabId}${field.sectionId}${dynamicFormInstanceId}`,
              field.id,
            );
            return {
              ...field,
              validators: [
                countryPostalCodeValidator(this.selectedCountry, tabId, field.sectionId, dynamicFormInstanceId),
              ],
            };
          }
          if (field.subType === constants.TEXT_SUB_TYPES.SOCIAL_SECURITY_NUMBER) {
            return {
              ...field,
              tooltipClass,
              tooltip:
                !this.isStepSubmitted &&
                ONBOARDING_HELP_TEXT[ONBOARDING_MONITORED_BLOCK_ENUM.SOCIAL_SECURITY_NUMBER].replace(
                  /{specialization_label}/g,
                  specialization_label,
                ),
              validators: [
                onlyDigitValidator(
                  'SSN_Rule',
                  9,
                  ONBOARDING_VALIDATION_MESSAGE[ONBOARDING_MONITORED_BLOCK_ENUM.SOCIAL_SECURITY_NUMBER],
                ),
              ],
            };
          }
          if (field.subType === constants.TEXT_SUB_TYPES.EMPLOYER_IDENTIFICATION_NUMBER) {
            return {
              ...field,
              tooltipClass,
              tooltip:
                !this.isStepSubmitted &&
                ONBOARDING_HELP_TEXT[ONBOARDING_MONITORED_BLOCK_ENUM.EMPLOYER_IDENTIFICATION_NUMBER].replace(
                  /{specialization_label}/g,
                  specialization_label,
                ),
              validators: [
                onlyDigitValidator(
                  'EIN_Rule',
                  9,
                  ONBOARDING_VALIDATION_MESSAGE[ONBOARDING_MONITORED_BLOCK_ENUM.EMPLOYER_IDENTIFICATION_NUMBER],
                ),
              ],
            };
          }
          if (field.subType === constants.TEXT_SUB_TYPES.NATIONAL_PROVIDER_IDENTIFIER) {
            return {
              ...field,
              tooltipClass,
              tooltip:
                !this.isStepSubmitted &&
                ONBOARDING_HELP_TEXT[ONBOARDING_MONITORED_BLOCK_ENUM.NATIONAL_PROVIDER_IDENTIFIER].replace(
                  /{specialization_label}/g,
                  specialization_label,
                ),
              validators: [
                onlyDigitValidator(
                  'NPI_Rule',
                  10,
                  ONBOARDING_VALIDATION_MESSAGE[ONBOARDING_MONITORED_BLOCK_ENUM.NATIONAL_PROVIDER_IDENTIFIER],
                ),
              ],
            };
          }
          if (field.subType === constants.TEXT_SUB_TYPES.LICENSE_NUMBER) {
            return {
              ...field,
              tooltipClass,
              tooltip:
                !this.isStepSubmitted &&
                ONBOARDING_VALIDATION_MESSAGE[ONBOARDING_MONITORED_BLOCK_ENUM.LICENSE_NUMBER].replace(
                  /{specialization_label}/g,
                  specialization_label,
                ),
              isWatchChangeValue: !this.isStepSubmitted,
              validators: [
                noSpacesValidator(
                  ONBOARDING_VALIDATION_MESSAGE[ONBOARDING_MONITORED_BLOCK_ENUM.LICENSE_NUMBER].replace(
                    /{specialization_label}/g,
                    this.step.specialization_label,
                  ),
                ),
              ],
            };
          }
        }
        if (field.type.id === constants.FORM_FIELD_TYPE_IDS.DATE) {
          if (field.subType === constants.DATE_SUB_TYPES.BIRTH_DATE) {
            return {
              ...field,
              validators: [birthDateValidator()],
            };
          }
          if (field.subType === constants.DATE_SUB_TYPES.EXPIRATION_DATE) {
            return {
              ...field,
              isWatchChangeValue: !this.isStepSubmitted,
            };
          }
        }
        return field;
      });
    }
  }

  updateFieldValue({ field: { id, value }, elements, controls }, tabId: ONBOARDING_TAB_ID, form) {
    const { dynamicFormInstanceId } = form;
    id = Number(id);
    const _getElementById = getElementById.bind(this, elements);
    const _getControlById = getControlById.bind(this, controls);

    const { sectionId } = _getElementById(id);

    const countrySelector = `${ONBOARDING_FIELD_IDS_MAP.COUNTRY}${tabId}${sectionId}${dynamicFormInstanceId}`;
    const countryIdFromMap = this.getFieldIdFromMap(countrySelector);

    const stateSelector = `${ONBOARDING_FIELD_IDS_MAP.STATE}${tabId}${sectionId}${dynamicFormInstanceId}`;
    const stateIdFromMap = this.getFieldIdFromMap(stateSelector);

    const obs$ = [];

    if (tabId === ONBOARDING_TAB_ID.JURISDICTION_INFORMATION) {
      if ([countryIdFromMap, stateIdFromMap].includes(id)) {
        const licenseInfoTab = this.tabs.find(({ id: _id }) => _id === ONBOARDING_TAB_ID.LICENSE_INFORMATION);
        if (
          licenseInfoTab.forms.find(
            ({ relatedDynamicFormInstanceId }) => relatedDynamicFormInstanceId === dynamicFormInstanceId,
          )
        ) {
          let fieldValueChanged;
          if (id === countryIdFromMap) {
            fieldValueChanged =
              !this.selectedCountry[tabId][sectionId] ||
              !this.selectedCountry[tabId][sectionId][dynamicFormInstanceId] ||
              this.selectedCountry[tabId][sectionId][dynamicFormInstanceId].id !== value;
          } else {
            fieldValueChanged = this.selectedCountry[tabId][sectionId][dynamicFormInstanceId].state !== value;
          }
          if (fieldValueChanged) {
            obs$.push(
              this.changeCountryStateConfirmWindow$().pipe(
                tap((confirm) => {
                  if (!confirm) {
                    const control = _getControlById(id);
                    let oldValue;
                    if (id === countryIdFromMap) {
                      oldValue = this.selectedCountry[tabId][sectionId][dynamicFormInstanceId].id;
                    } else {
                      oldValue = this.selectedCountry[tabId][sectionId][dynamicFormInstanceId].state;
                    }
                    control.setValue(oldValue, { emitEvent: false });
                  } else {
                    licenseInfoTab.forms = licenseInfoTab.forms.filter(
                      ({ relatedDynamicFormInstanceId }) => relatedDynamicFormInstanceId !== dynamicFormInstanceId,
                    );
                    if (!licenseInfoTab.forms.length) {
                      this.addAnotherForm(ONBOARDING_TAB_ID.LICENSE_INFORMATION);
                    }
                  }
                }),
                switchMap((confirm) => (confirm ? of(null) : NEVER)),
              ),
            );
          }
        }
      }
    }
    forkJoin(obs$).subscribe({
      complete: () => {
        if (id === countryIdFromMap) {
          //  Country was changed
          const countryField = _getElementById(countryIdFromMap);
          const selectedCountry = countryField.elementOptions.find(
            ({ id: elementOption }) => elementOption === Number(value),
          );
          if (selectedCountry) {
            const isCountryChanged =
              !this.selectedCountry[tabId][sectionId] ||
              !this.selectedCountry[tabId][sectionId][dynamicFormInstanceId] ||
              this.selectedCountry[tabId][sectionId][dynamicFormInstanceId].id !== selectedCountry.id;
            const oldState = this.selectedCountry[tabId][sectionId][dynamicFormInstanceId].state;
            this.selectedCountry[tabId][sectionId][dynamicFormInstanceId] = {
              ...selectedCountry,
              state: oldState,
            };
            if (!isCountryChanged && countryField.ignoreIsPushInitValue) {
              return;
            }
            if (selectedCountry && countryField.sectionTypeId !== SECTION_TYPE.LICENSE_ADDRESS_INFORMATION) {
              if (tabId === ONBOARDING_TAB_ID.JURISDICTION_INFORMATION) {
                this.allowedData.selectedCountyNames.push(selectedCountry.name || selectedCountry.value);
              } else if (
                tabId === ONBOARDING_TAB_ID.LICENSE_INFORMATION &&
                this.allowedData.selectedCountyNames.length
              ) {
                countryField.elementOptions = countryField.elementOptions.filter(({ name }) =>
                  this.allowedData.selectedCountyNames.includes(name),
                );
              }
            }
            // Update ZipCode
            const zipCodeIdFromMap = this.getFieldIdFromMap(
              `${ONBOARDING_FIELD_IDS_MAP.ZIP_CODE}${tabId}${sectionId}${dynamicFormInstanceId}`,
            );
            if (zipCodeIdFromMap) {
              const zipCodeControls = _getControlById(zipCodeIdFromMap);
              zipCodeControls.setValue(zipCodeControls.value);
            }
            // State
            const stateField = _getElementById(stateIdFromMap);
            if (
              this.selectedCountry[tabId][sectionId][dynamicFormInstanceId].name &&
              stateField &&
              stateField.fieldInstanceId
            ) {
              const stateControls = _getControlById(stateIdFromMap);
              const countryName = this.selectedCountry[tabId][sectionId][dynamicFormInstanceId].name;
              if (tabId === ONBOARDING_TAB_ID.JURISDICTION_INFORMATION) {
                if (!this.jurisdictionHelpObject.subject$) {
                  this.jurisdictionHelpObject.subject$ = new Subject();
                  this.jurisdictionHelpObject.dynamicFormInstanceIds = [dynamicFormInstanceId];
                } else {
                  this.jurisdictionHelpObject.dynamicFormInstanceIds.push(dynamicFormInstanceId);
                }
              }
              this.stepService
                .getFieldInstanceOptions(stateField.fieldInstanceId, {
                  countryName,
                })
                .pipe(
                  delayWhen(() => {
                    if (tabId === ONBOARDING_TAB_ID.LICENSE_INFORMATION && this.jurisdictionHelpObject.subject$) {
                      return this.jurisdictionHelpObject.subject$;
                    }
                    return of(null);
                  }),
                )
                .subscribe((response) => {
                  const selectedCountryName = selectedCountry.name || selectedCountry.value;
                  const elementOptionsUnfiltered = response.map(({ optionId, name, stateId }) => ({
                    id: optionId,
                    name,
                    stateId,
                  }));
                  if (!this.jurisdictionHelpObject.elementOptionsByFieldInstanceId[stateField.fieldInstanceId]) {
                    this.jurisdictionHelpObject.elementOptionsByFieldInstanceId[stateField.fieldInstanceId] = {};
                  }
                  this.jurisdictionHelpObject.elementOptionsByFieldInstanceId[stateField.fieldInstanceId][countryName] =
                    elementOptionsUnfiltered;
                  let elementOptions = _cloneDeep(elementOptionsUnfiltered);
                  if (stateField.sectionTypeId !== SECTION_TYPE.LICENSE_ADDRESS_INFORMATION) {
                    if (tabId === ONBOARDING_TAB_ID.JURISDICTION_INFORMATION) {
                      elementOptions = elementOptions.filter(({ stateId }) =>
                        this.allowedData[selectedCountryName].allowedStateIds.includes(stateId),
                      );
                    } else if (tabId === ONBOARDING_TAB_ID.LICENSE_INFORMATION) {
                      if (!form.isNational) {
                        elementOptions = elementOptions.filter(({ stateId }) =>
                          this.allowedData[selectedCountryName].allowedStateIds.includes(stateId),
                        );
                      } else {
                        elementOptions = elementOptions.filter(({ stateId }) =>
                          this.allowedData[selectedCountryName].zNationalStateIds.includes(stateId),
                        );
                      }
                    } else {
                      elementOptions = elementOptions.filter(
                        ({ stateId }) =>
                          !this.allowedData[selectedCountryName] ||
                          !this.allowedData[selectedCountryName].zNationalStateIds.includes(stateId),
                      );
                    }
                    if (tabId === ONBOARDING_TAB_ID.JURISDICTION_INFORMATION) {
                      this.jurisdictionHelpObject.dynamicFormInstanceIds =
                        this.jurisdictionHelpObject.dynamicFormInstanceIds.filter(
                          (_id) => _id !== dynamicFormInstanceId,
                        );
                      if (!this.jurisdictionHelpObject.dynamicFormInstanceIds.length) {
                        setTimeout(() => {
                          this.jurisdictionHelpObject.subject$.complete();
                          delete this.jurisdictionHelpObject.subject$;
                        });
                      }
                    }
                    if (tabId === ONBOARDING_TAB_ID.LICENSE_INFORMATION) {
                      elementOptions = elementOptions.filter(({ stateId }) => {
                        return this.allowedData[countryName].selectedStateIds.includes(stateId);
                      });
                    }
                  } else {
                    elementOptions = elementOptions.filter(
                      ({ stateId }) =>
                        !this.allowedData[selectedCountryName] ||
                        !this.allowedData[selectedCountryName].zNationalStateIds.includes(stateId),
                    );
                  }
                  stateField.elementOptions = elementOptions;
                  const stateValue = stateControls.value;

                  if (isCountryChanged) {
                    stateControls.setValue(null);
                    this.selectedCountry[tabId][sectionId][dynamicFormInstanceId].state = null;
                  } else if (stateValue) {
                    let zNationalState = false;
                    if (this.allowedData[selectedCountryName]) {
                      const { zNationalStateIds } = this.allowedData[selectedCountryName];
                      if (zNationalStateIds.length) {
                        zNationalState = response.reduce((res, state) => {
                          return res || (zNationalStateIds.includes(state.stateId) && stateValue === state.optionId);
                        }, false);
                      }
                    }
                    if (zNationalState) {
                      form.isNational = true;
                      if (tabId === ONBOARDING_TAB_ID.JURISDICTION_INFORMATION) {
                        stateField.elementOptions = elementOptionsUnfiltered;
                      } else if (
                        tabId === ONBOARDING_TAB_ID.LICENSE_INFORMATION &&
                        stateField.sectionTypeId !== SECTION_TYPE.LICENSE_ADDRESS_INFORMATION
                      ) {
                        stateField.label = ZNATIONAL_JURISDICTION_LABEL;
                        stateField.elementOptions = elementOptionsUnfiltered.filter(({ stateId }) =>
                          this.allowedData[selectedCountryName].zNationalStateIds.includes(stateId),
                        );
                      }
                    }
                    if (tabId === ONBOARDING_TAB_ID.JURISDICTION_INFORMATION) {
                      if (stateValue) {
                        this.allowedData.selectedCountyNames.push(countryName);
                        this.allowedData[countryName].selected = true;
                        this.allowedData[countryName].selectedStateIds.push(
                          stateField.elementOptions.find(({ id: _id }) => _id === stateValue).stateId,
                        );
                      }
                    }
                    this.selectedCountry[tabId][sectionId][dynamicFormInstanceId].state = stateValue;
                    // waiting for options to load
                    setTimeout(() => stateControls.setValue(stateValue));
                  }
                  if (!this.isStepSubmitted) {
                    if (response.length && !stateField.disabled) {
                      stateControls.enable({ emitEvent: false });
                    } else {
                      stateControls.disable({ emitEvent: false });
                    }
                  }
                });
            }
          }
        } else if (id === stateIdFromMap) {
          // State was changed
          const stateField = _getElementById(stateIdFromMap);
          const selectedState = stateField.elementOptions.find(
            ({ id: elementOption }) => elementOption === Number(value),
          );
          if (tabId === ONBOARDING_TAB_ID.JURISDICTION_INFORMATION && selectedState && selectedState.stateId) {
            this.allowedData.allowedLicenseTypesByStateId[selectedState.stateId].dynamicFormInstanceId =
              dynamicFormInstanceId;
          }
          // License Type
          const licenseTypeIdFromMap = this.getFieldIdFromMap(
            `${ONBOARDING_FIELD_IDS_MAP.LICENSE_TYPE}${tabId}${sectionId}${dynamicFormInstanceId}`,
          );
          const licenseTypeField = _getElementById(licenseTypeIdFromMap);
          // if licenseType field exists in the same form
          if (licenseTypeField && licenseTypeField.fieldInstanceId) {
            const licenseTypeControls = _getControlById(licenseTypeIdFromMap);
            if (!selectedState) {
              licenseTypeControls.setValue(null);
              licenseTypeControls.disable();
            } else if (selectedState && selectedState.stateId) {
              const countryControl = _getControlById(countryIdFromMap);
              const countryField = _getElementById(countryIdFromMap);
              const stateControl = _getControlById(stateIdFromMap);
              if (tabId === ONBOARDING_TAB_ID.LICENSE_INFORMATION) {
                const selectedStateStateId = selectedState.stateId;
                try {
                  if (this.allowedData.allowedLicenseTypesByStateId[selectedStateStateId].dynamicFormInstanceId) {
                    form.relatedDynamicFormInstanceId =
                      this.allowedData.allowedLicenseTypesByStateId[selectedStateStateId].dynamicFormInstanceId;
                  }
                } catch (e) {}
              }
              const isStateChanged =
                this.selectedCountry[tabId][sectionId][dynamicFormInstanceId].state !== selectedState.id;
              if (!isStateChanged && stateField.ignoreIsPushInitValue) {
                return;
              }
              const licenseTypeValue = licenseTypeControls.value;
              let elementOptions;
              const { allowed, required } = this.allowedData.allowedLicenseTypesByStateId[selectedState.stateId];
              if (!isStateChanged && licenseTypeValue && required.find(({ id: _id }) => _id === licenseTypeValue)) {
                licenseTypeField.disabled = true;
                countryField.disabled = true;
                stateField.disabled = true;
                countryControl.disable({ emitEvent: false });
                stateControl.disable({ emitEvent: false });
                form.required = true;
                elementOptions = required;
              } else {
                elementOptions = allowed;
              }
              if (!elementOptions.length) {
                elementOptions.push({
                  useHtml: true,
                  htmlMarkup: `<span class="fa fa-exclamation-triangle mr-2 color-orange"></span>No options available. Please select a different state or remove this ${this.step.specialization_label}.`,
                  disabled: true,
                });
              }

              licenseTypeField.elementOptions = elementOptions;
              if (isStateChanged) {
                licenseTypeControls.setValue(null);
              } else if (licenseTypeValue) {
                // waiting for options to load
                setTimeout(() => licenseTypeControls.setValue(licenseTypeValue));
              }
              if (!this.isStepSubmitted) {
                if (!licenseTypeField.disabled) {
                  licenseTypeControls.enable();
                } else {
                  licenseTypeControls.disable();
                }
              }
            }
          }
          this.selectedCountry[tabId][sectionId][dynamicFormInstanceId].state = selectedState ? selectedState.id : null;
        } else if (!this.isStepSubmitted) {
          // other fields whose value does not depend on other fields
          const element = _getElementById(id);
          const control = _getControlById(id);
          const { type, subType } = element;
          if (type) {
            switch (type.id) {
              case constants.FORM_FIELD_TYPE_IDS.DATE:
                if (subType === constants.DATE_SUB_TYPES.EXPIRATION_DATE) {
                  if (control.value && moment().diff(value, 'days') > 0) {
                    this.changeDetector.detach();
                    return this.smartModalService
                      .openConfirm({
                        title: 'Warning',
                        body: ONBOARDING_VALIDATION_MESSAGE[ONBOARDING_MONITORED_BLOCK_ENUM.EXPIRATION_DATE].replace(
                          /{specialization_label}/g,
                          this.step.specialization_label,
                        ),
                        successButtonText: 'Confirm',
                      })
                      .subscribe((isConfirm) => {
                        this.changeDetector.reattach();
                        this.changeDetector.detectChanges();
                        if (!isConfirm) {
                          control.setValue(null);
                        }
                      });
                  }
                }
                break;
              case constants.FORM_FIELD_TYPE_IDS.TEXT:
                if (subType === constants.TEXT_SUB_TYPES.LICENSE_NUMBER) {
                  if (new RegExp(/\s/).test(control.value)) {
                    control.setValue(control.value.replace(/\s/g, ''));
                  }
                }
                break;
            }
          }
        }
      },
    });
  }

  private changeCountryStateConfirmWindow$() {
    return this.smartModalService
      .openConfirm({
        title: 'WARNING',
        body: `If you remove or change a country/state where you provide service, we will remove any ${this.step.specialization_label} information you have provided on the following screen associated with that country/state.`,
        successButtonText: 'Continue',
      })
      .pipe(take(1));
  }

  private setFieldIdMap(key: string, id: number): void {
    this.fieldsIds.set(key, id);
  }

  private getFieldIdFromMap(key: string): number | null {
    if (!this.fieldsIds) {
      return null;
    }
    return this.fieldsIds.get(key);
  }

  @ComputedFrom('tabs', 'activeTabId')
  get isLastTab(): boolean {
    return this.tabs.length ? _last(this.tabs).id.toString() === this.activeTabId : false;
  }

  @ComputedFrom('tabs', 'activeTabId')
  get isFirstTab(): boolean {
    return this.tabs.length ? this.tabs[0].id.toString() === this.activeTabId : false;
  }

  private completeStep(payload, saveAsDraft = false) {
    const dataToSend: OnboardingCompletePayload = {
      specificTypePayload: {
        ...payload,
        saveAsDraft,
        signature_link: this.signature_link,
        onboardingFormStepInstanceId: this.step.step_instance_id,
      },
      stepTypeId: STEP_TYPES.ONBOARDING,
    };
    const precondition$ = saveAsDraft
      ? of(null)
      : this.smartModalService
          .openConfirm({
            title: 'WARNING',
            body: this.submitMessage,
            rejectButtonText: 'Back',
            successButtonText: 'Confirm',
          })
          .pipe(
            take(1),
            filter((confirmed) => confirmed),
          );
    precondition$
      .pipe(
        switchMap(() => {
          return this.stepService.submitStepWithSpecificTypePayload(this.step.step_instance_id, dataToSend);
        }),
      )
      .subscribe(() => {
        this.stepCompletedMessage(saveAsDraft);
      });
  }

  private isVerificationRequired() {
    return [VERIFICATION_TYPE.VERIFICATION_REQUIRED, VERIFICATION_TYPE.DOUBLE_VERIFICATION_REQUIRED].includes(
      this.step.verificationType,
    );
  }

  private stepCompletedMessage(saveAsDraft) {
    const messageText = saveAsDraft
      ? 'Information has been successfully saved'
      : this.isVerificationRequired()
      ? 'Step has been submitted for review'
      : 'Step has been successfully completed';
    this.messageQueryService.addMessage(messageText);
    this.activeModal.close();
  }

  @ComputedFrom('step')
  get jurisdictionTabTitle() {
    return ONBOARDING_HELP_TEXT[ONBOARDING_MONITORED_BLOCK_ENUM.LICENSE_JURISDICTION].replace(
      /{specialization_label}/g,
      this.step.specialization_label,
    );
  }

  @ComputedFrom('step')
  get addNationalCredentialTooltip() {
    return ONBOARDING_HELP_TEXT[ONBOARDING_MONITORED_BLOCK_ENUM.ADD_NATIONAL_CREDENTIAL].replace(
      /{specialization_label}/g,
      this.step.specialization_label,
    );
  }

  @ComputedFrom('step')
  get jurisdictionNextScreenNotificationText() {
    return ONBOARDING_HELP_TEXT[ONBOARDING_MONITORED_BLOCK_ENUM.JURISDICTION_NEXT_SCREEN_NOTIFICATION].replace(
      /{specialization_label}/g,
      this.step.specialization_label,
    );
  }

  @ComputedFrom('step')
  get jurisdictionNextScreenNationalNotificationText() {
    return ONBOARDING_HELP_TEXT[ONBOARDING_MONITORED_BLOCK_ENUM.JURISDICTION_NEXT_SCREEN_NATIONAL_NOTIFICATION].replace(
      /{specialization_label}/g,
      this.step.specialization_label,
    );
  }
}
